import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Factory,
  ShoppingCart,
  Truck,
  Store,
  Building2,
  X,
  ChevronLeft,
  ChevronDown,
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Layers,
  Wallet,
  ShoppingBag,
  FileBarChart,
  Package,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { hasAccess } from '../../lib/routes';
import { cn } from '../../utils/cn';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  permission: string;
  children?: NavItem[];
}

// Definición de navegación con grupos
const navigation: NavItem[] = [
  {
    name: 'Comercial',
    icon: ShoppingCart,
    permission: 'pedidos',
    children: [
      {
        name: 'Dashboard',
        href: '/dashboard-comercial',
        icon: BarChart3,
        permission: 'dashboard-comercial',
      },
      {
        name: 'Pedidos',
        href: '/pedidos',
        icon: ShoppingCart,
        permission: 'pedidos',
      },
      {
        name: 'Despachos',
        href: '/despachos',
        icon: Truck,
        permission: 'despachos',
      },
      {
        name: 'Mercadeo',
        href: '/mercadeo',
        icon: Store,
        permission: 'mercadeo',
      },
      {
        name: 'Cadenas',
        href: '/clientes/cadenas',
        icon: Building2,
        permission: 'clientes',
      },
    ],
  },
  {
    name: 'Producción',
    icon: Factory,
    permission: 'batch',
    children: [
      {
        name: 'Batch',
        href: '/batch',
        icon: Layers,
        permission: 'batch',
      },
      {
        name: 'KPI Producción',
        href: '/kpi-produccion',
        icon: BarChart3,
        permission: 'kpi-produccion',
      },
      {
        name: 'Cronograma',
        href: '/cronograma',
        icon: Calendar,
        permission: 'cronograma',
      },
    ],
  },
  {
    name: 'Sistema de Calidad',
    icon: ClipboardCheck,
    permission: 'gestion-calidad',
    children: [
      {
        name: 'Registros',
        href: '/registros',
        icon: ClipboardList,
        permission: 'registros',
      },
      {
        name: 'Gestión Documental',
        href: '/gestion-calidad',
        icon: ClipboardCheck,
        permission: 'gestion-calidad',
      },
    ],
  },
  {
    name: 'Cartera',
    href: '/cartera',
    icon: Wallet,
    permission: 'cartera',
  },
  {
    name: 'Compras',
    href: '/compras',
    icon: ShoppingBag,
    permission: 'compras',
  },
  {
    name: 'Reportes',
    href: '/reportes',
    icon: FileBarChart,
    permission: 'reportes',
  },
  {
    name: 'Catálogo',
    href: '/catalogo',
    icon: Package,
    permission: 'catalogo',
  },
];

export default function Sidebar({
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
}: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    // Auto-expand if current route is in a group
    const expanded = new Set<string>();
    navigation.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some(
          (child) => child.href && location.pathname.startsWith(child.href)
        );
        if (isChildActive) {
          expanded.add(item.name);
        }
      }
    });
    return expanded;
  });

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Filtrar navegación según permisos del usuario
  // Para grupos con hijos, mostrar si tiene acceso a al menos un hijo
  const filteredNavigation = navigation.filter((item) =>
    item.children
      ? item.children.some((child) => hasAccess(user?.rol, child.permission))
      : hasAccess(user?.rol, item.permission)
  );

  return (
    <>
      {/* Overlay móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200',
          'transform transition-all duration-300 ease-in-out lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'lg:w-20' : 'lg:w-64',
          'w-64'
        )}
      >
        {/* Header del Sidebar */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <div className={cn('flex items-center gap-3', isCollapsed && 'lg:justify-center')}>
            {/* Logo */}
            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-white">AZ</span>
            </div>

            {/* Nombre empresa */}
            <div className={cn(isCollapsed && 'lg:hidden')}>
              <h1 className="font-bold text-gray-900">Azalac</h1>
              <p className="text-xs text-gray-500">Yogurt Griego</p>
            </div>
          </div>

          {/* Botón cerrar móvil */}
          <button
            className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
            onClick={onClose}
            aria-label="Cerrar menú"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Botón colapsar desktop */}
          <button
            className={cn(
              'hidden lg:flex p-2 rounded-lg hover:bg-gray-100 transition-transform',
              isCollapsed && 'rotate-180'
            )}
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto py-4 custom-scrollbar">
          <ul className="space-y-1 px-3">
            {filteredNavigation.map((item) => {
              // Si tiene hijos, renderizar como grupo expandible
              if (item.children) {
                const isExpanded = expandedGroups.has(item.name);

                return (
                  <li key={item.name}>
                    {/* Header del grupo */}
                    <button
                      onClick={() => toggleGroup(item.name)}
                      className={cn(
                        'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                        isCollapsed && 'lg:justify-center lg:px-0'
                      )}
                      title={isCollapsed ? item.name : undefined}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        <span className={cn(isCollapsed && 'lg:hidden')}>
                          {item.name}
                        </span>
                      </div>
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform',
                          isExpanded && 'rotate-180',
                          isCollapsed && 'lg:hidden'
                        )}
                      />
                    </button>

                    {/* Children */}
                    {isExpanded && !isCollapsed && (
                      <ul className="mt-1 ml-4 pl-4 border-l border-gray-200 space-y-1">
                        {item.children
                          .filter((child) => hasAccess(user?.rol, child.permission))
                          .map((child) => (
                            <li key={child.name}>
                              <NavLink
                                to={child.href!}
                                onClick={onClose}
                                className={({ isActive }) =>
                                  cn(
                                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                                    isActive
                                      ? 'bg-gray-100 text-gray-900 font-medium'
                                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                                  )
                                }
                              >
                                <child.icon className="w-4 h-4 flex-shrink-0" />
                                <span>{child.name}</span>
                              </NavLink>
                            </li>
                          ))}
                      </ul>
                    )}
                  </li>
                );
              }

              // Item normal sin hijos
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href!}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                        isCollapsed && 'lg:justify-center lg:px-0'
                      )
                    }
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className={cn(isCollapsed && 'lg:hidden')}>
                      {item.name}
                    </span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer del Sidebar */}
        <div className={cn(
          'p-4 border-t border-gray-200',
          isCollapsed && 'lg:flex lg:justify-center'
        )}>
          <div className={cn(
            'text-xs text-gray-400',
            isCollapsed && 'lg:hidden'
          )}>
            <p>v1.0.0</p>
          </div>
        </div>
      </aside>
    </>
  );
}
